import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp, Bug } from 'lucide-react';
import { captureAppError } from '../monitoring/sentry';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
    showDetails: boolean;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        captureAppError(error);
        // Log detailed error to console
        console.group('%c🚨 Application Error', 'color: #ef4444; font-size: 16px; font-weight: bold;');
        console.error('%cError:', 'color: #ef4444; font-weight: bold;', error.message);
        console.error('%cStack:', 'color: #f97316; font-weight: bold;', error.stack);
        console.error('%cComponent Stack:', 'color: #3b82f6; font-weight: bold;', errorInfo.componentStack);
        console.groupEnd();

        this.setState({ errorInfo });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    toggleDetails = () => {
        this.setState(prev => ({ showDetails: !prev.showDetails }));
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            const { error, errorInfo, showDetails } = this.state;

            return (
                <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4 text-center font-sans">
                    <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                        {/* Header with icon */}
                        <div className="h-48 bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent opacity-90" />
                            <div className="relative z-10 flex flex-col items-center">
                                <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg mb-4">
                                    <Bug className="w-12 h-12 text-white" />
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-8 relative z-20 -mt-8">
                            <h1 className="text-2xl font-bold text-gray-900 mb-3">
                                Oups ! Une erreur s'est produite
                            </h1>
                            <p className="text-gray-500 text-base mb-6 leading-relaxed">
                                L'application a rencontré un problème inattendu. Nos bricoleurs travaillent dessus !
                            </p>

                            {/* Error Details (Always expandable in dev, show button in prod) */}
                            {error && (
                                <div className="mb-6 text-left">
                                    <button
                                        onClick={this.toggleDetails}
                                        className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors mb-2"
                                    >
                                        {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                        Détails techniques
                                    </button>

                                    {showDetails && (
                                        <div className="bg-gray-900 p-4 rounded-xl border border-gray-700 overflow-hidden">
                                            <div className="mb-3">
                                                <span className="text-red-400 font-mono text-xs uppercase tracking-wider">Erreur:</span>
                                                <pre className="mt-1 text-sm text-red-300 overflow-auto whitespace-pre-wrap font-mono break-all">
                                                    {error.message}
                                                </pre>
                                            </div>

                                            {error.stack && (
                                                <div className="mb-3">
                                                    <span className="text-orange-400 font-mono text-xs uppercase tracking-wider">Stack:</span>
                                                    <pre className="mt-1 text-xs text-gray-400 overflow-auto whitespace-pre-wrap font-mono max-h-40">
                                                        {error.stack}
                                                    </pre>
                                                </div>
                                            )}

                                            {errorInfo?.componentStack && (
                                                <div>
                                                    <span className="text-blue-400 font-mono text-xs uppercase tracking-wider">Component Stack:</span>
                                                    <pre className="mt-1 text-xs text-gray-500 overflow-auto whitespace-pre-wrap font-mono max-h-32">
                                                        {errorInfo.componentStack}
                                                    </pre>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={this.handleRetry}
                                    className="flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                >
                                    <RefreshCw size={20} />
                                    Réessayer
                                </button>
                                <button
                                    onClick={this.handleGoHome}
                                    className="flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition"
                                >
                                    <Home size={20} />
                                    Accueil
                                </button>
                            </div>
                        </div>
                    </div>

                    <p className="mt-8 text-gray-400 text-sm">
                        🔧 Bricoler c'est aussi réparer les bugs
                    </p>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
